import React, { useEffect, useRef, useState } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface PublicChatWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'en' | 'es';
}

const UI_COPY = {
  en: {
    title: 'Car Buying Assistant',
    subtitle: 'Ask us anything',
    placeholder: 'Type your question...',
    greeting: "Hi! I'm here to help with your car buying questions. What would you like to know?",
    error: 'Sorry, something went wrong. Please try again.',
    thinking: 'Thinking...',
  },
  es: {
    title: 'Asistente de Compra',
    subtitle: 'Preguntanos lo que quieras',
    placeholder: 'Escribe tu pregunta...',
    greeting:
      '\u00a1Hola! Estoy aqu\u00ed para ayudarte con tus preguntas sobre la compra de autos. \u00bfQu\u00e9 deseas saber?',
    error: 'Lo sentimos, algo sali\u00f3 mal. Por favor intenta de nuevo.',
    thinking: 'Pensando...',
  },
};

const PublicChatWidget: React.FC<PublicChatWidgetProps> = ({ isOpen, onClose, language }) => {
  const copy = UI_COPY[language];
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: copy.greeting },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMsg: Message = { role: 'user', content: trimmed };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setIsStreaming(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch('/api/v1/public/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          history: messages.slice(-10).map((m) => ({ role: m.role, content: m.content })),
          language,
        }),
      });

      if (!response.ok) throw new Error('Chat request failed');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No response body');

      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: next[next.length - 1].content + parsed.text,
                };
                return next;
              });
            }
          } catch {
            /* ignore parse errors */
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: copy.error };
        return next;
      });
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-20 right-4 z-50 flex flex-col w-80 md:w-96 rounded-2xl shadow-2xl border border-slate-200 bg-white overflow-hidden"
      style={{ maxHeight: '480px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-blue-700 px-4 py-3">
        <div>
          <p className="text-sm font-bold text-white leading-none">{copy.title}</p>
          <p className="text-xs text-blue-200 mt-0.5">{copy.subtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="text-blue-200 hover:text-white transition-colors"
          aria-label="Close chat"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
              }`}
            >
              {msg.content ||
                (isStreaming && i === messages.length - 1 ? (
                  <span className="text-slate-400 italic">{copy.thinking}</span>
                ) : null)}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-3 border-t border-slate-200 bg-white">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={copy.placeholder}
          disabled={isStreaming}
          className="flex-1 text-sm rounded-full border border-slate-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-slate-100"
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isStreaming}
          className="rounded-full bg-blue-600 p-2 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Send message"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PublicChatWidget;
